import React from 'react';
import {
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './components/theme-provider';
import { ProtectedRoute } from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Import from './pages/Import';
import Team from './pages/Team';
import { Toaster } from './components/ui/toaster';
import { PageSkeleton } from './components/ui/skeleton';
import { lazyWithReload, ChunkErrorBoundary } from './lib/lazyWithReload';

// SpreadsheetView is by far the heaviest page — split it (and its settings)
// out of the main bundle; Dashboard stays eager as the landing page
const SpreadsheetView = lazyWithReload(() => import('./pages/SpreadsheetView'));
const SpreadsheetSettings = lazyWithReload(
  () => import('./pages/SpreadsheetSettings')
);

// V2 pages are code-split so they don't grow the main bundle
const ClientsV2 = lazyWithReload(() => import('./pages/ClientsV2'));
const WorkflowV2 = lazyWithReload(() => import('./pages/WorkflowV2'));
const EmailV2 = lazyWithReload(() => import('./pages/EmailV2'));
const BankDataV2 = lazyWithReload(() => import('./pages/BankDataV2'));
const SalesTaxV2 = lazyWithReload(() => import('./pages/SalesTaxV2'));
const AchPayorsV2 = lazyWithReload(() => import('./pages/AchPayorsV2'));
const FavoriteLinksV2 = lazyWithReload(() => import('./pages/FavoriteLinksV2'));
const CalendarV2 = lazyWithReload(() => import('./pages/CalendarV2'));
const CheckInV2 = lazyWithReload(() => import('./pages/CheckInV2'));
const LlcFilingV2 = lazyWithReload(() => import('./pages/LlcFilingV2'));
const ExtensionsV2 = lazyWithReload(() => import('./pages/ExtensionsV2'));
const BookkeepingNotEfiledV2 = lazyWithReload(
  () => import('./pages/BookkeepingNotEfiledV2')
);

function V2Suspense({ children }: { children: React.ReactNode }) {
  return (
    <ChunkErrorBoundary>
      <React.Suspense fallback={<PageSkeleton tabs={false} />}>
        {children}
      </React.Suspense>
    </ChunkErrorBoundary>
  );
}

// Redirect component for old spreadsheet URLs
function SpreadsheetRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/spreadsheets/${id}`} replace />;
}

// Redirect old /email-v2/* URLs to /email/* keeping the subpath
function EmailV2Redirect() {
  const location = useLocation();
  return (
    <Navigate to={location.pathname.replace(/^\/email-v2/, '/email')} replace />
  );
}

export default function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="tifpa-ui-theme">
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />

          {/* Protected routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<Dashboard />} />
            <Route
              path="spreadsheets/:id?"
              element={
                <V2Suspense>
                  <SpreadsheetView />
                </V2Suspense>
              }
            />
            <Route
              path="spreadsheets/:id/settings"
              element={
                <V2Suspense>
                  <SpreadsheetSettings />
                </V2Suspense>
              }
            />
            {/* Redirect old spreadsheet route for backwards compatibility */}
            <Route path="spreadsheet/:id" element={<SpreadsheetRedirect />} />
            {/* Calendar V2 replaced the V1 page 2026-08-06 */}
            <Route
              path="calendar"
              element={
                <V2Suspense>
                  <CalendarV2 />
                </V2Suspense>
              }
            />
            {/* Redirect for the old V2 preview URL */}
            <Route
              path="calendar-v2"
              element={<Navigate to="/calendar" replace />}
            />
            <Route
              path="clients/*"
              element={
                <V2Suspense>
                  <ClientsV2 />
                </V2Suspense>
              }
            />
            {/* Redirects for the old V2 preview URLs */}
            <Route
              path="clients-v2"
              element={<Navigate to="/clients" replace />}
            />
            <Route path="email-v2/*" element={<EmailV2Redirect />} />
            <Route
              path="workflow-v2"
              element={
                <V2Suspense>
                  <WorkflowV2 />
                </V2Suspense>
              }
            />
            <Route
              path="bank-data-v2/*"
              element={
                <V2Suspense>
                  <BankDataV2 />
                </V2Suspense>
              }
            />
            <Route
              path="sales-tax-v2"
              element={
                <V2Suspense>
                  <SalesTaxV2 />
                </V2Suspense>
              }
            />
            <Route
              path="llc-filing-v2"
              element={
                <V2Suspense>
                  <LlcFilingV2 />
                </V2Suspense>
              }
            />
            <Route
              path="extensions-v2"
              element={
                <V2Suspense>
                  <ExtensionsV2 />
                </V2Suspense>
              }
            />
            <Route
              path="bookkeeping-not-efiled-v2"
              element={
                <V2Suspense>
                  <BookkeepingNotEfiledV2 />
                </V2Suspense>
              }
            />
            {/* ACH Payments retired 2026-08-05 — old links land on Payors */}
            <Route
              path="ach-payments"
              element={<Navigate to="/ach-payors" replace />}
            />
            <Route
              path="ach-payors"
              element={
                <V2Suspense>
                  <AchPayorsV2 />
                </V2Suspense>
              }
            />
            <Route
              path="favorite-links"
              element={
                <V2Suspense>
                  <FavoriteLinksV2 />
                </V2Suspense>
              }
            />
            <Route path="settings" element={<Settings />} />
            <Route path="import" element={<Import />} />
            <Route path="team" element={<Team />} />
            <Route
              path="email/*"
              element={
                <V2Suspense>
                  <EmailV2 />
                </V2Suspense>
              }
            />
            {/* Legacy bank pages removed — everything lives in Bank Data V2 */}
            <Route
              path="bank-data/*"
              element={<Navigate to="/bank-data-v2" replace />}
            />
            {/* Legacy Check-In pages removed — everything lives in Appointments */}
            <Route
              path="check-in/*"
              element={<Navigate to="/appointments" replace />}
            />
            <Route
              path="appointments"
              element={
                <V2Suspense>
                  <CheckInV2 />
                </V2Suspense>
              }
            />
            <Route path="" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
        <Toaster />
      </AuthProvider>
    </ThemeProvider>
  );
}
